/* eslint-disable import/order */
/* eslint-disable @typescript-eslint/no-unused-vars */
import api from './axios';
import {
  Reunion,
  FilterReunionDto,
  CreateReunionDto,
  CreateReunionAutoDto,
  ReunionEstado,
  ReunionParticipante,
  CreateParticipanteDto,
  UpdateParticipanteDto,
  AgendarSiguienteReunionDto,
  CadenaReunionResponseDto,
  FilterHistorialReunionDto,
} from '../types/reuniones';

const BASE_URL = '/reuniones';

// List meetings with filters
export async function fetchReuniones(filters: FilterReunionDto = {}): Promise<Reunion[]> {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });

  const { data } = await api.get(`${BASE_URL}?${params.toString()}`);
  return data;
}

// Get single meeting
export async function fetchReunion(id: number): Promise<Reunion> {
  const { data } = await api.get(`${BASE_URL}/${id}`);
  return data;
}

// Create manual meeting
export async function createReunion(payload: CreateReunionDto): Promise<Reunion> {
  const { data } = await api.post(BASE_URL, payload);
  return data;
}

// Create auto-scheduled meeting
export async function createReunionAuto(payload: CreateReunionAutoDto): Promise<Reunion> {
  const { data } = await api.post(`${BASE_URL}/auto`, payload);
  return data;
}

// Update meeting
export async function updateReunion(
  id: number,
  payload: Partial<CreateReunionDto>,
): Promise<Reunion> {
  const { data } = await api.put(`${BASE_URL}/${id}`, payload);
  return data;
}

// Update meeting state only
export async function updateReunionEstado(
  id: number,
  estado: ReunionEstado,
  descripcion?: string,
): Promise<Reunion> {
  const { data } = await api.patch(`${BASE_URL}/${id}/estado`, { estado, descripcion });
  return data;
}

// Delete meeting
export async function deleteReunion(id: number): Promise<void> {
  await api.delete(`${BASE_URL}/${id}`);
}

// Get meeting logs
export async function fetchReunionLogs(id: number): Promise<any[]> {
  const { data } = await api.get(`${BASE_URL}/${id}/logs`);
  return data;
}

// Statistics and reporting
export async function fetchReunionesPorAgenteMes(
  ano?: number,
  mes?: number,
  idStaff?: number,
): Promise<any> {
  const params = new URLSearchParams();
  if (ano) params.append('ano', ano.toString());
  if (mes) params.append('mes', mes.toString());
  if (idStaff) params.append('idStaff', idStaff.toString());

  const { data } = await api.get(`${BASE_URL}/kpis/reuniones-por-agente-mes?${params.toString()}`);
  return data;
}

export async function fetchNoShowPorTipo(fechaDesde?: string, fechaHasta?: string): Promise<any> {
  const params = new URLSearchParams();
  if (fechaDesde) params.append('fechaDesde', fechaDesde);
  if (fechaHasta) params.append('fechaHasta', fechaHasta);

  const { data } = await api.get(`${BASE_URL}/kpis/no-shows-por-tipo?${params.toString()}`);
  return data;
}

/* ══════════════════════════════════════════════════════════════
   PARTICIPANTES
 ══════════════════════════════════════════════════════════════ */

export async function fetchParticipantesReunion(idReunion: number): Promise<ReunionParticipante[]> {
  const { data } = await api.get(`${BASE_URL}/${idReunion}/participantes`);
  return data;
}

export async function agregarParticipanteReunion(
  idReunion: number,
  payload: CreateParticipanteDto,
): Promise<ReunionParticipante> {
  const { data } = await api.post(`${BASE_URL}/${idReunion}/participantes`, payload);
  return data;
}

export async function eliminarParticipanteReunion(
  idReunion: number,
  idUsuario: number,
): Promise<void> {
  await api.delete(`${BASE_URL}/${idReunion}/participantes/${idUsuario}`);
}

export async function actualizarEstadoParticipante(
  idReunion: number,
  idUsuario: number,
  estado: string, // ReunionParticipanteEstado
): Promise<ReunionParticipante> {
  const { data } = await api.patch(`${BASE_URL}/${idReunion}/participantes/${idUsuario}/estado`, {
    estado,
  });
  return data;
}

/* ══════════════════════════════════════════════════════════════
   REUNIONES EN CADENA
 ══════════════════════════════════════════════════════════════ */

// Schedule next meeting in chain
export async function agendarSiguienteReunion(
  id: number,
  payload: AgendarSiguienteReunionDto,
): Promise<Reunion> {
  const { data } = await api.post(`${BASE_URL}/${id}/siguiente`, payload);
  return data;
}

// Re-schedule meeting automatically (no-show / no-answer)
export async function reagendarReunion(
  id: number,
): Promise<{ wc1: Reunion | null; wc2: Reunion | null }> {
  const { data } = await api.post(`${BASE_URL}/${id}/reagendar`);
  return data;
}

// Get meeting chain (anterior, actual, siguiente)
export async function obtenerCadenaReunion(id: number): Promise<CadenaReunionResponseDto> {
  const { data } = await api.get(`${BASE_URL}/${id}/cadena`);
  return data;
}

// Get meeting history by service
export async function obtenerHistorialServicio(
  filters: FilterHistorialReunionDto,
): Promise<Reunion[]> {
  const params = new URLSearchParams();
  params.append('alumnoId', filters.alumnoId.toString());
  params.append('servicioId', filters.servicioId.toString());
  if (filters.fechaDesde) params.append('fechaDesde', filters.fechaDesde);
  if (filters.fechaHasta) params.append('fechaHasta', filters.fechaHasta);

  const { data } = await api.get(`${BASE_URL}/historial?${params.toString()}`);
  return data;
}

// Reporte de seguimiento por servicio
export interface ReporteSeguimientoServicio {
  servicio: {
    id: number;
    nombre: string;
  };
  alumno: {
    id: number;
    nombre: string;
  };
  periodo: {
    desde: string | null;
    hasta: string | null;
  };
  metricas: {
    totalReuniones: number;
    realizadas: number;
    canceladas: number;
    programadas: number;
    noShows: number;
    tasaRealizacion: number;
    tasaCancelacion: number;
    frecuenciaPromedioDias: number | null;
  };
  ultimaReunion: {
    id: number;
    fechaInicio: string;
    fechaFin: string;
    estado: string;
    staffNombre: string;
    canal: string;
  } | null;
  primeraReunion: {
    id: number;
    fechaInicio: string;
    fechaFin: string;
    estado: string;
    staffNombre: string;
    canal: string;
  } | null;
  tendencia: Array<{
    mes: number;
    ano: number;
    total: number;
    realizadas: number;
    canceladas: number;
  }>;
  reuniones: Array<{
    id: number;
    fechaInicio: string;
    fechaFin: string;
    estado: string;
    staffNombre: string;
    canal: string;
    idReunionAnterior: number | null;
    idReunionSiguiente: number | null;
  }>;
}

export async function fetchReporteSeguimientoServicio(
  alumnoId: number,
  servicioId: number,
  desde?: string,
  hasta?: string,
): Promise<ReporteSeguimientoServicio> {
  const params = new URLSearchParams();
  params.append('alumnoId', String(alumnoId));
  params.append('servicioId', String(servicioId));
  if (desde) params.append('desde', desde);
  if (hasta) params.append('hasta', hasta);

  const { data } = await api.get(`${BASE_URL}/reportes/seguimiento-servicio?${params.toString()}`);
  return data;
}
